import { useCallback, useState } from 'react'
import { message } from 'antd'
import { collection, doc, writeBatch } from 'firebase/firestore'
import db from '../firebase'
import { buildNotificationPayload } from './bulkEmailTemplate'

const BATCH_SIZE = 400

const chunk = (items, size) => {
  const chunks = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

export const useSendBulkNotification = () => {
  const [loading, setLoading] = useState(false)

  const sendBulkEmails = useCallback(async ({ recipients, subject, body }) => {
    const validRecipients = recipients.filter(recipient => recipient.email)
    const skippedCount = recipients.length - validRecipients.length

    if (!validRecipients.length) {
      message.error('No selected employees have an email address.')
      return { sent: 0, skipped: skippedCount }
    }

    setLoading(true)

    try {
      const notificationsRef = collection(db, 'notifications')
      const batches = chunk(validRecipients, BATCH_SIZE)

      for (const recipientBatch of batches) {
        const batch = writeBatch(db)

        recipientBatch.forEach((recipient) => {
          const notificationRef = doc(notificationsRef)
          batch.set(notificationRef, buildNotificationPayload({
            email: recipient.email,
            firstName: recipient.firstName,
            lastName: recipient.lastName,
            subject,
            bodyTemplate: body
          }))
        })

        await batch.commit()
      }

      if (skippedCount) {
        message.warning(`Queued ${validRecipients.length} email${validRecipients.length === 1 ? '' : 's'}. Skipped ${skippedCount} without an email address.`)
      } else {
        message.success(`Queued ${validRecipients.length} email${validRecipients.length === 1 ? '' : 's'}.`)
      }

      return { sent: validRecipients.length, skipped: skippedCount }
    } catch (error) {
      message.error(error?.message || 'Failed to queue bulk emails.')
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  return { sendBulkEmails, loading }
}
